import React, { useState, useEffect } from 'react';
import { 
  Gift, 
  Copy, 
  Check, 
  Users, 
  DollarSign, 
  Clock, 
  Loader2,
  Share2,
  Percent,
  ExternalLink,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const apiRequest = async (endpoint) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) throw new Error('Request failed');
  return response.json();
};

export const ReferralProgram = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  // Gerar link dinamicamente usando a URL atual do browser
  const getReferralLink = () => {
    if (stats?.codigo_indicacao) {
      return `${window.location.origin}/checkout?ref=${stats.codigo_indicacao}`;
    }
    return '';
  };

  const referralLink = getReferralLink();

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/indicacao/estatisticas');
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const link = getReferralLink();
    if (link) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareWhatsApp = () => {
    const link = getReferralLink();
    if (link) {
      const texto = `Olá! Estou usando o Conforme a Resolução para gerenciar a conformidade da minha farmácia e está sendo incrível! Use meu link e comece seu período de teste grátis: ${link}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-purple-200 dark:border-purple-800">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-xl">
            <Gift className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <CardTitle className="text-xl text-purple-900 dark:text-purple-100">
              Programa de Indicação
            </CardTitle>
            <CardDescription className="text-purple-700 dark:text-purple-300">
              Indique amigos e ganhe 20% de desconto na próxima mensalidade!
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-6">
        {/* Seu Link */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Seu Link de Indicação
          </label>
          <div className="flex gap-2">
            <Input 
              value={referralLink} 
              readOnly 
              className="font-mono text-sm bg-slate-50 dark:bg-slate-800"
            />
            <Button
              onClick={copyLink}
              variant="outline"
              className={copied ? 'bg-green-50 border-green-300 text-green-700' : ''}
            >
              {copied ? (
                <Check className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            <Button
              onClick={shareWhatsApp}
              className="bg-green-600 hover:bg-green-700"
            >
              <Share2 className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Código: <span className="font-bold text-purple-600">{stats?.codigo_indicacao}</span>
          </p>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.total_indicacoes || 0}</p>
            <p className="text-xs text-slate-500">Indicações</p>
          </div>
          
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-center">
            <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.convertidas || 0}</p>
            <p className="text-xs text-slate-500">Convertidas</p>
          </div>
          
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-center">
            <Clock className="w-6 h-6 mx-auto mb-2 text-amber-600" />
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.pendentes || 0}</p>
            <p className="text-xs text-slate-500">Pendentes</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg p-4 text-center text-white">
            <Percent className="w-6 h-6 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.creditos_disponiveis || 0}</p>
            <p className="text-xs opacity-90">Descontos Disponíveis</p>
          </div>
        </div>

        {/* Desconto Disponível */}
        {stats?.creditos_disponiveis > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-full">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-bold text-green-800 dark:text-green-200">
                  🎉 Você tem {stats.creditos_disponiveis} desconto(s) de {stats.desconto_percentual}% disponível(is)!
                </p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  O desconto será aplicado automaticamente na sua próxima renovação.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Como funciona */}
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
          <h4 className="font-medium text-purple-900 dark:text-purple-200 mb-3">
            🎁 Como funciona:
          </h4>
          <ol className="text-sm text-purple-800 dark:text-purple-300 space-y-2 list-decimal list-inside">
            <li>Compartilhe seu link com outros donos de farmácias</li>
            <li>Quando eles se cadastrarem pelo seu link, você será vinculado como indicador</li>
            <li>Se o indicado efetuar o primeiro pagamento, você ganha <strong>20% de desconto</strong> na próxima mensalidade</li>
            <li>O desconto é aplicado automaticamente!</li>
          </ol>
        </div>

        {/* Histórico de Indicações */}
        {stats?.indicacoes_recentes?.length > 0 && (
          <div>
            <h4 className="font-medium text-slate-900 dark:text-white mb-3">
              Histórico de Indicações
            </h4>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Farmácia</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.indicacoes_recentes.map((ind, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {ind.farmacia_indicada_nome || 'N/A'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(ind.data_indicacao).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-center">
                        {ind.status === 'convertido' ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Convertido
                          </Badge>
                        ) : ind.status === 'pendente' ? (
                          <Badge variant="outline" className="text-amber-600 border-amber-200">
                            <Clock className="w-3 h-3 mr-1" />
                            Aguardando
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">
                            {ind.status}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralProgram;
